// src/pages/OrganizationUsers.js
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import { AuthContext } from '../contexts/AuthContext';
import {
  apiFetch,
  post as apiPost,
  put as apiPut,
  delete as apiDelete,
  fetchOrganizations,
  getSelectedOrgId,
  setSelectedOrgId,
} from '../services/api';

export default function OrganizationUsers() {
  const { user } = useContext(AuthContext);
  const role = (user?.group_role || '').toLowerCase();
  const isSuper = role === 'superadmin';
  const isAdmin = role === 'admin';
  const notAllowed = !isSuper && !isAdmin;

  // --- Page-level org filter (superadmin: remembers selection; admin: fixed to own org) ---
  const initialOrgFilter = isSuper ? getSelectedOrgId() : (user?.organization_id ?? null);
  const [orgFilter, setOrgFilter] = useState(initialOrgFilter);
  const [orgs, setOrgs] = useState([]);

  // --- Data ---
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  // --- Filters/search ---
  const [search, setSearch] = useState('');

  // --- Form ---
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    organization_id: isSuper ? (initialOrgFilter ?? '') : null,
    user_name: '',
    username: '',
    user_email: '',
    user_group_id: '',
    status: 1,
    password: '', // optional on edit
  });

  // ---------- helpers ----------
  const fetchScopedList = useCallback(
    async (basePath, orgIdForScope) => {
      try {
        if (isSuper) {
          if (orgIdForScope == null) {
            const all = await apiFetch(`${basePath}?all=1`).catch(() => []);
            return Array.isArray(all) ? all : [];
          }
          let data = await apiFetch(`${basePath}?orgId=${orgIdForScope}`).catch(() => null);
          if (Array.isArray(data)) return data;

          data = await apiFetch(`${basePath}?organizationId=${orgIdForScope}`).catch(() => null);
          if (Array.isArray(data)) return data;

          data = await apiFetch(`${basePath}?organization_id=${orgIdForScope}`).catch(() => null);
          if (Array.isArray(data)) return data;

          const all = await apiFetch(`${basePath}?all=1`).catch(() => []);
          return (Array.isArray(all) ? all : []).filter(
            (u) => Number(u.organization_id) === Number(orgIdForScope)
          );
        } else {
          const own = await apiFetch(basePath).catch(() => []);
          return Array.isArray(own) ? own : [];
        }
      } catch {
        return [];
      }
    },
    [isSuper]
  );

  const groupNameById = useMemo(() => {
    const m = new Map();
    (groups || []).forEach((g) => m.set(g.user_group_id ?? g.id, g.user_group_name ?? g.name));
    return m;
  }, [groups]);

  const visible = useMemo(() => {
    let rows = Array.isArray(users) ? users : [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (u) =>
          (u.user_name || '').toLowerCase().includes(q) ||
          (u.user_email || '').toLowerCase().includes(q) ||
          (u.username || '').toLowerCase().includes(q) ||
          (groupNameById.get(u.user_group_id) || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [users, search, groupNameById]);

  // ---------- effects ----------
  // Load orgs list (only needed for superadmin)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSuper) return;
      const data = await fetchOrganizations().catch(() => []);
      if (!cancelled) setOrgs(Array.isArray(data) ? data : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuper]);

  // Persist/read org filter across tabs for superadmin
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selectedOrgId') setOrgFilter(getSelectedOrgId());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Load groups & users
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [gs, us] = await Promise.all([
        apiFetch('/api/user-groups').catch(() => []),
        fetchScopedList('/api/users', isSuper ? orgFilter : null),
      ]);
      if (!cancelled) {
        setGroups(Array.isArray(gs) ? gs : []);
        setUsers(Array.isArray(us) ? us : []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchScopedList, isSuper, orgFilter]);

  // ---------- actions ----------
  const startCreate = () => {
    setEditingId(null);
    setForm({
      organization_id: isSuper ? (orgFilter ?? '') : null,
      user_name: '',
      username: '',
      user_email: '',
      user_group_id: '',
      status: 1,
      password: '',
    });
  };

  const startEdit = (u) => {
    setEditingId(u.user_id);
    setForm({
      organization_id: isSuper ? (u.organization_id ?? orgFilter ?? '') : null,
      user_name: u.user_name ?? '',
      username: u.username ?? '',
      user_email: u.user_email ?? '',
      user_group_id: u.user_group_id ?? '',
      status: Number(u.status ?? 1),
      password: '',
    });
  };

  const buildSaveUrl = (id, effectiveOrgId) => {
    if (!isSuper) return id ? `/api/users/${id}` : `/api/users`;
    const qs =
      effectiveOrgId != null && effectiveOrgId !== ''
        ? `?orgId=${encodeURIComponent(effectiveOrgId)}`
        : orgFilter != null
        ? `?orgId=${encodeURIComponent(orgFilter)}`
        : '';
    return id ? `/api/users/${id}${qs}` : `/api/users${qs}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCreate = !editingId;
    const effectiveOrgId = isSuper
      ? form.organization_id === ''
        ? null
        : Number(form.organization_id)
      : null;

    if (!form.user_name || !form.username || !form.user_email || !form.user_group_id) {
      return alert('Please fill in Name, Username, Email, and Group.');
    }
    if (isCreate && !isSuper && !user?.organization_id) {
      return alert('Your user does not have an organization_id.');
    }

    const payload = {
      user_name: form.user_name.trim(),
      username: form.username.trim(),
      user_email: form.user_email.trim(),
      user_group_id: Number(form.user_group_id),
      status: Number(form.status),
    };
    if (isSuper && effectiveOrgId != null) payload.organization_id = effectiveOrgId;
    if (form.password && form.password.trim().length > 0) payload.password = form.password.trim();

    try {
      if (editingId) {
        await apiPut(buildSaveUrl(editingId, effectiveOrgId), payload);
      } else {
        await apiPost(buildSaveUrl(null, effectiveOrgId), payload);
      }
      const us = await fetchScopedList('/api/users', isSuper ? orgFilter : null);
      setUsers(Array.isArray(us) ? us : []);
      startCreate();
    } catch (err) {
      console.error('save user failed:', err);
      alert('Failed to save user.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await apiDelete(`/api/users/${id}`);
      setUsers((rows) => (rows || []).filter((r) => r.user_id !== id));
    } catch (err) {
      console.error('delete user failed:', err);
      alert('Failed to delete user.');
    }
  };

  // ---------- render ----------
  // NOTE: We render "not authorized" view here AFTER all hooks are called to satisfy rules-of-hooks.
  if (notAllowed) {
    return (
      <div>
        <Navbar />
        <div className="p-8">
          <h1 className="text-2xl font-bold mb-2">Organization Users</h1>
          <div className="text-gray-600">You are not authorized to view this page.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold">Organization Users</h1>
          <div className="ml-auto flex items-center gap-2">
            {isSuper && (
              <>
                <label className="text-sm text-gray-500">Organization:</label>
                <select
                  className="border p-2 rounded"
                  value={orgFilter ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = v === '' ? null : Number(v);
                    setOrgFilter(next);
                    setSelectedOrgId(next);
                    setForm((f) => ({ ...f, organization_id: next ?? '' }));
                  }}
                >
                  <option value="">All</option>
                  {(orgs || []).map((o) => (
                    <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                      {o.name ?? o.organization_name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <button
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              onClick={startCreate}
            >
              + New User
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-2">
          <input
            className="border p-2 rounded w-full md:w-96"
            placeholder="Search users."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mb-6 bg-white rounded shadow p-4 grid gap-3 md:grid-cols-2">
          {isSuper && (
            <div>
              <label className="block text-sm text-gray-600 mb-1">Organization</label>
              <select
                className="border p-2 rounded w-full"
                value={form.organization_id ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    organization_id: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
              >
                <option value="">(none)</option>
                {(orgs || []).map((o) => (
                  <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                    {o.name ?? o.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-600 mb-1">Name</label>
            <input
              className="border p-2 rounded w-full"
              value={form.user_name}
              onChange={(e) => setForm((f) => ({ ...f, user_name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Username</label>
            <input
              className="border p-2 rounded w-full"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Email</label>
            <input
              type="email"
              className="border p-2 rounded w-full"
              value={form.user_email}
              onChange={(e) => setForm((f) => ({ ...f, user_email: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Group</label>
            <select
              className="border p-2 rounded w-full"
              value={form.user_group_id}
              onChange={(e) => setForm((f) => ({ ...f, user_group_id: e.target.value }))}
            >
              <option value="">Select group</option>
              {(groups || []).map((g) => (
                <option key={g.user_group_id ?? g.id} value={g.user_group_id ?? g.id}>
                  {g.user_group_name ?? g.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              className="border p-2 rounded w-full"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Password</label>
            <input
              type="password"
              className="border p-2 rounded w-full"
              placeholder={editingId ? '(leave blank to keep current password)' : ''}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2 flex gap-2">
            <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              {editingId ? 'Save Changes' : 'Create User'}
            </button>
            {editingId && (
              <button
                type="button"
                className="border px-4 py-2 rounded"
                onClick={startCreate}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Table */}
        <table className="w-full bg-white rounded shadow text-left">
          <thead>
            <tr>
              <th className="p-2 border-b">ID</th>
              <th className="p-2 border-b">Name</th>
              <th className="p-2 border-b">Username</th>
              <th className="p-2 border-b">Email</th>
              <th className="p-2 border-b">Group</th>
              <th className="p-2 border-b">Status</th>
              {isSuper && <th className="p-2 border-b">Organization</th>}
              <th className="p-2 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(visible || []).map((u) => (
              <tr key={u.user_id} className="hover:bg-gray-50">
                <td className="p-2 border-b">{u.user_id}</td>
                <td className="p-2 border-b">{u.user_name}</td>
                <td className="p-2 border-b">{u.username}</td>
                <td className="p-2 border-b">{u.user_email}</td>
                <td className="p-2 border-b">
                  {groupNameById.get(u.user_group_id) || u.user_group_id}
                </td>
                <td className="p-2 border-b">{Number(u.status) === 1 ? 'Active' : 'Inactive'}</td>
                {isSuper && (
                  <td className="p-2 border-b">
                    {new Map((orgs || []).map((o) => [o.id ?? o.organization_id, o.name ?? o.organization_name]))
                      .get(u.organization_id) ||
                      (u.organization_id ? `#${u.organization_id}` : '-')}
                  </td>
                )}
                <td className="p-2 border-b">
                  <button className="text-blue-700 mr-3" onClick={() => startEdit(u)}>
                    Edit
                  </button>
                  <button className="text-red-700" onClick={() => remove(u.user_id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!visible || visible.length === 0) && (
              <tr>
                <td className="p-3 text-gray-500" colSpan={isSuper ? 8 : 7}>
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
